import { useTranslation } from "react-i18next";
import { Input, PhoneInput } from "@unified-checkout/design-system";
import type { SocialMediaProps } from "@app/dashboard/types/innerLayout/business_profile";
import styles from "../business-profile.module.scss";
import { extractPhoneNumberWithoutCode, validateUrlField, validateWhatsappPhone } from "../utils";

export const SocialMedia = ({
  formState,
  formErrors,
  setFormState,
  setFormErrors,
}: SocialMediaProps) => {
  const { t } = useTranslation();

  const phoneNumberWithoutCode = extractPhoneNumberWithoutCode(
    formState.whatsappPhone || '',
    formState.whatsappCountry
  );

  const updateField = <K extends keyof typeof formState>(field: K, value: (typeof formState)[K]) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleWhatsappChange = (phone: string, country: string) => {
    updateField("whatsappPhone", phone);
    updateField("whatsappCountry", country);
    validateWhatsappPhone(phone, country, setFormErrors, t);
  };

  const handleWhatsappBlur = () => {
    validateWhatsappPhone(formState.whatsappPhone, formState.whatsappCountry, setFormErrors, t);
  };

  const handleUrlBlur = (fieldName: 'website' | 'facebook' | 'instagram', value: string) => {
    validateUrlField(fieldName, value, setFormErrors, t);
  };

  return (
    <div className={styles["business-profile__section"]}>
      <div className={styles["business-profile__section__label"]}>
        <p>{t('innerLayout.businessProfilePage.socialMedia.title')}</p>
      </div>
      <div className={styles["business-profile__section__form"]}>
        <div className={styles["business-profile__section__form__content"]}>
          <div className={styles["business-profile__field"]}>
            <PhoneInput
              name="whatsappPhone"
              label={t('innerLayout.businessProfilePage.socialMedia.whatsappPhone.label')}
              country={formState.whatsappCountry}
              phoneNumber={phoneNumberWithoutCode}
              onChangeHandler={(phone, country) => {
                handleWhatsappChange(phone, country || 'eg');
              }}
              onBlur={handleWhatsappBlur}
              error={formErrors.whatsappPhone}
            />
          </div>

          <div className={styles["business-profile__field"]}>
            <Input
              name="website"
              label={t('innerLayout.businessProfilePage.socialMedia.website.label')}
              placeholder={t('innerLayout.businessProfilePage.socialMedia.website.placeholder')}
              value={formState.website}
              onChange={(e) => updateField("website", e.target.value)}
              onBlur={(e) => handleUrlBlur("website", e.target.value)}
              error={formErrors.website}
              status={formErrors.website ? 'error' : 'default'}
            />
          </div>

          <div className={styles["business-profile__field"]}>
            <Input
              name="facebook"
              label={t('innerLayout.businessProfilePage.socialMedia.facebook.label')}
              placeholder={t('innerLayout.businessProfilePage.socialMedia.facebook.placeholder')}
              value={formState.facebook}
              onChange={(e) => updateField("facebook", e.target.value)}
              onBlur={(e) => handleUrlBlur("facebook", e.target.value)}
              error={formErrors.facebook}
              status={formErrors.facebook ? 'error' : 'default'}
            />
          </div>

          <div className={styles["business-profile__field"]}>
            <Input
              name="instagram"
              label={t('innerLayout.businessProfilePage.socialMedia.instagram.label')}
              placeholder={t('innerLayout.businessProfilePage.socialMedia.instagram.placeholder')}
              value={formState.instagram}
              onChange={(e) => updateField("instagram", e.target.value)}
              onBlur={(e) => handleUrlBlur("instagram", e.target.value)}
              error={formErrors.instagram}
              status={formErrors.instagram ? 'error' : 'default'}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
